import { settingsRoutes } from "@/app";
import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { ResizableHandle } from "@/components/flexkit/Resizable";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { Nudge } from "@/components/nudges/Nudge";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { nonSettingsPageNavigationEntryPredicate } from "@/components/settings/settings";
import { SettingsMenu, USER_SETTINGS_MOVED_DISMISSED_KEY } from "@/components/settings/SettingsMenu";
import { ResizableSidebar } from "@/components/SidebarLayout";
import { Tooltip } from "@/components/Tooltip";

import { useLastNavigationEntry } from "@/hooks/use-last-navigation-entry";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { TrackLocations } from "@/hooks/use-segment";
import type { MenuItem } from "@/menu-item";
import { populateMenuFromRoute } from "@/menu-item";
import { useCallback, useMemo, type FC } from "react";

export const SettingsSidebar: FC<{ className?: string; minWidth?: number; maxWidth?: number }> = ({
    className,
    minWidth,
    maxWidth,
}) => {
    return (
        <ResizableSidebar className={className} minWidth={minWidth} maxWidth={maxWidth}>
            <div data-testid="sidebar" className="flex size-full flex-col pb-[6px] pt-0.5">
                <div className="px-4 pt-2">
                    <SidebarHeader />
                </div>
                <ScrollArea orientation="vertical" className="mr-0.5 flex-grow pr-[1px] pt-2">
                    <SidebarContent />
                </ScrollArea>
                <SettingsSidebarFooter />
            </div>
            <ResizableHandle className="top-4 mr-[1px] h-[calc(100%-32px)]" />
        </ResizableSidebar>
    );
};

const SidebarHeader: FC = () => {
    const lastVisitedNonSettingsPage = useLastNavigationEntry(nonSettingsPageNavigationEntryPredicate);

    return (
        <Tooltip
            content={
                <div className="flex flex-row items-center gap-2">
                    Back to Ona
                    <KeyboardCombo keys={["Esc"]} />
                </div>
            }
        >
            <Link
                className="flex h-8 flex-row items-center gap-1 rounded-lg hover:bg-surface-03"
                to={lastVisitedNonSettingsPage}
                reloadIfFrontendUpdated={true}
            >
                <div className="w-5">
                    <IconArrowLeft size="base" />
                </div>

                <div className={cn("flex-grow truncate text-start text-base font-bold")}>Back to Ona</div>
            </Link>
        </Tooltip>
    );
};

const SidebarContent: FC = () => {
    const menu: MenuItem[] = useMemo(() => populateMenuFromRoute(settingsRoutes), []);
    return <SettingsMenu menu={menu} />;
};

const SettingsSidebarFooter: FC = () => {
    const { value: dismissed, setValue: setDismissed } = usePreferenceValue(USER_SETTINGS_MOVED_DISMISSED_KEY);

    const handleDismiss = useCallback(() => {
        setDismissed("true");
    }, [setDismissed]);

    const isNudgeOpen = !dismissed;

    return (
        <div className="flex flex-col gap-2 p-2 pb-2 pt-4">
            <UserSettingsMovedNudge isOpen={isNudgeOpen} onDismiss={handleDismiss} />
            <div
                className={cn("rounded-lg", {
                    "animate-nudge-ripple shadow-[0_0_0_1px_rgb(var(--content-brand)/0.5)] [animation-delay:0.3s] [animation-iteration-count:3]":
                        isNudgeOpen,
                })}
            >
                <OrgSwitcher
                    data-track-location={TrackLocations.SettingsSidebarOrgSwitcher}
                    hideKeyboardShortcuts
                    isLocked
                />
            </div>
        </div>
    );
};

const UserSettingsMovedNudge: FC<{ isOpen: boolean; onDismiss: () => void }> = ({ isOpen, onDismiss }) => {
    return (
        <Nudge
            open={isOpen}
            toggleOpen={onDismiss}
            ariaLabelPrefix="User settings moved"
            data-testid="nudge-user-settings-moved"
            showCloseButton={false}
            className="max-w-none"
        >
            <div className="flex flex-col gap-2">
                <div className="p-2 text-center">
                    <Text className="text-base font-bold text-content-primary">User settings moved</Text>
                    <Text className="text-base text-content-secondary">
                        Your personal settings are now accessible from the organization selector below.
                    </Text>
                </div>
                <Button
                    variant="primary"
                    size="md"
                    className="grow"
                    onClick={onDismiss}
                    data-tracking-id="user-settings-moved-got-it"
                >
                    Got it!
                </Button>
            </div>
        </Nudge>
    );
};
